export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  unit?: string;
  productKey?: string; // ClaveProdServ
  taxObject?: string; // ObjetoImp
}

// Payment program type
export type PaymentProgram = 'standard' | 'pronto_pago';

// Pronto Pago fee configuration
export const PRONTO_PAGO_FEE_RATE = 0.08; // 8%

// Late invoice reason type
export type LateInvoiceReason =
  | 'after_deadline'
  | 'wrong_invoice_date'
  | 'wrong_week_in_description';

// Pronto Pago preview for UI
export interface ProntoPagoPreview {
  originalAmount: number;
  feeAmount: number;
  netAmount: number;
}

// CFDI type discriminator
export type CfdiType = 'I' | 'E' | 'P' | 'N' | 'T';

// Credit Note data (CFDI tipo E - Egreso)
export interface CreditNoteData {
  uuid: string;
  folio?: string;
  series?: string;
  issuerRfc: string;
  issuerName?: string;
  relatedUuid: string;
  tipoRelacion: string;
  subtotal: number;
  totalTax: number;
  totalAmount: number;
  currency: string;
  issueDate: string;
  certificationDate?: string;
  tipoComprobante: string;
}

// Credit Note validation result
export interface CreditNoteValidation {
  isValid: boolean;
  errors: string[];
  data?: CreditNoteData;
}

// Payment Complement data (CFDI tipo P - Pago)
export interface PaymentComplementData {
  uuid: string;
  folio?: string;
  series?: string;
  issuerRfc: string;
  issuerName?: string;
  relatedUuids: string[];
  tipoComprobante: string;
  paymentDate?: string;
  paymentForm?: string;
  amount?: number;
  currency: string;
  partialityNumber?: number;
  previousBalance?: number;
  amountPaid?: number;
  remainingBalance?: number;
  issueDate: string;
  certificationDate?: string;
}

// Payment Complement validation result
export interface PaymentComplementValidation {
  isValid: boolean;
  errors: string[];
  data?: PaymentComplementData;
}

export interface InvoiceData {
  // Selection fields
  week: string; // The current week number (for filing)
  year: number;
  expectedWeek: number; // The expected billing week (previous week)
  weekFromDescription?: number; // Week extracted from invoice description by AI
  project: string;
  needsProjectReview?: boolean; // Flag for manual project review

  // Late invoice flags
  isLate: boolean;
  lateReasons: LateInvoiceReason[]; // Can have multiple reasons
  lateAcknowledged: boolean;

  // Issuer (Emisor) fields
  issuerRfc: string;
  issuerName: string;
  issuerRegime: string;
  issuerZipCode?: string; // CP del emisor

  // Receiver (Receptor) fields
  receiverRfc: string;
  receiverName?: string;
  receiverRegime: string;
  receiverZipCode?: string; // CP del receptor
  cfdiUse?: string; // UsoCFDI

  // Invoice identification
  invoiceDate: string;
  folio: string;
  series?: string; // Serie
  uuid: string;
  certificationDate?: string; // FechaTimbrado
  satCertNumber?: string; // NoCertificadoSAT

  // Payment info
  paymentMethod: 'PUE' | 'PPD' | '';
  paymentForm?: string; // FormaPago (03 = Transferencia)
  paymentConditions?: string; // CondicionesDePago

  // Financial fields
  subtotal: string;
  totalTax: string;
  retentionIva: string;
  retentionIvaRate: number;
  retentionIsr: string;
  retentionIsrRate: number;
  totalAmount: string;
  currency: string;
  exchangeRate?: string; // TipoCambio

  // Payment program (Pronto Pago)
  paymentProgram: PaymentProgram;
  prontoPagoFeeRate: number;
  prontoPagoFeeAmount: number;
  netPaymentAmount: number;

  // Items
  items: InvoiceItem[];

  // Contact info
  email: string;
  phoneNumber: string;

  // Files
  xmlFile: File | null;
  pdfFile: File | null;

  // Credit Note files (required for Pronto Pago)
  creditNoteXmlFile: File | null;
  creditNotePdfFile: File | null;
  creditNoteData: CreditNoteData | null;
  creditNoteValidation: CreditNoteValidation | null;

  // Payment Complement files (optional, for PPD invoices)
  hasComplement: boolean;
  complementXmlFile: File | null;
  complementPdfFile: File | null;
  complementData: PaymentComplementData | null;
  complementValidation: PaymentComplementValidation | null;
}

export enum ProjectType {
  // Proyectos vigentes (ordenados alfabéticamente)
  NOVENTA_Y_NUEVE_MINUTOS = '99 MINUTOS',
  AMAZON = 'AMAZON',
  APYMSA = 'APYMSA',
  CARSO_T1 = 'CARSO T1',
  COPPEL = 'COPPEL',
  DINAMICA_FILMICA = 'DINAMICA FILMICA',
  ELEKTRA = 'ELEKTRA',
  MERCADO_LIBRE = 'MERCADO LIBRE',
  OFFICE_DEPOT = 'OFFICE DEPOT',
  RAPPI_TURBO = 'RAPPI TURBO',
  SAMS = 'SAMS',
  SODIMAC = 'SODIMAC',
  SPEEDIANCE = 'SPEEDIANCE',
  WALMART = 'WALMART',
  OTHER = 'OTRO',
}

export interface ExtractionResult {
  weekFromDescription?: number; // Week number extracted from invoice concept
  weekConfidence?: number; // 0.0 - 1.0
  project?: string;
  projectConfidence?: number; // 0.0 - 1.0
  needsProjectReview?: boolean;

  // Issuer
  issuerRfc?: string;
  issuerName?: string;
  issuerRegime?: string;
  issuerZipCode?: string;

  // Receiver
  receiverRfc?: string;
  receiverName?: string;
  receiverRegime?: string;
  receiverZipCode?: string;
  cfdiUse?: string;

  // Contact
  email?: string;

  // Invoice ID
  invoiceDate?: string;
  folio?: string;
  series?: string;
  uuid?: string;
  certificationDate?: string;
  satCertNumber?: string;

  // Payment
  paymentMethod?: 'PUE' | 'PPD';
  paymentForm?: string;
  paymentConditions?: string;

  // Financial
  subtotal?: number;
  totalTax?: number;
  retentionIva?: number;
  retentionIvaRate?: number;
  retentionIsr?: number;
  retentionIsrRate?: number;
  totalAmount?: number;
  currency?: string;
  exchangeRate?: string;

  // Items
  items?: InvoiceItem[];
}

// Payload to send to webhook
export interface WebhookPayload {
  // Metadata
  submittedAt: string;
  week: number;
  year: number;
  project: string;

  // Late invoice info
  isLate: boolean;
  lateReason?: LateInvoiceReason; // Primary reason for backwards compatibility
  lateReasons: LateInvoiceReason[]; // All reasons
  lateAcknowledgedAt?: string;

  // Issuer (Emisor)
  issuer: {
    rfc: string;
    name: string;
    regime: string;
    zipCode?: string;
  };

  // Receiver (Receptor)
  receiver: {
    rfc: string;
    name?: string;
    regime: string;
    zipCode?: string;
    cfdiUse?: string;
  };

  // Invoice identification
  invoice: {
    uuid: string;
    folio: string;
    series?: string;
    date: string;
    certificationDate?: string;
    satCertNumber?: string;
  };

  // Payment details
  payment: {
    method: string;
    form?: string;
    conditions?: string;
  };

  // Financial summary
  financial: {
    subtotal: number;
    totalTax: number;
    retentionIva: number;
    retentionIvaRate: number;
    retentionIsr: number;
    retentionIsrRate: number;
    totalAmount: number;
    currency: string;
    exchangeRate?: string;
  };

  // Payment program (Pronto Pago)
  paymentProgram: {
    program: PaymentProgram;
    feeRate: number;
    feeAmount: number;
    netAmount: number;
  };

  // Line items
  items: InvoiceItem[];

  // Contact
  contact: {
    email: string;
    phone: string;
  };

  // Files in Base64
  files: {
    xml: {
      name: string;
      content: string; // Base64
      mimeType: string;
    } | null;
    pdf: {
      name: string;
      content: string; // Base64
      mimeType: string;
    } | null;
  };

  // Credit Note (required for Pronto Pago)
  creditNote?: {
    uuid: string;
    folio?: string;
    series?: string;
    relatedUuid: string;
    tipoRelacion: string;
    issuerRfc: string;
    issuerName?: string;
    subtotal: number;
    totalTax: number;
    totalAmount: number;
    currency: string;
    issueDate: string;
    certificationDate?: string;
    files: {
      xml: {
        name: string;
        content: string;
        mimeType: string;
      } | null;
      pdf: {
        name: string;
        content: string;
        mimeType: string;
      } | null;
    };
  };

  // Payment Complement (optional, for PPD invoices)
  complement?: {
    uuid: string;
    folio?: string;
    series?: string;
    relatedUuids: string[];
    issuerRfc: string;
    issuerName?: string;
    paymentDate?: string;
    paymentForm?: string;
    amount?: number;
    currency: string;
    partialityNumber?: number;
    previousBalance?: number;
    amountPaid?: number;
    remainingBalance?: number;
    issueDate: string;
    certificationDate?: string;
    files: {
      xml: {
        name: string;
        content: string;
        mimeType: string;
      } | null;
      pdf: {
        name: string;
        content: string;
        mimeType: string;
      } | null;
    };
  };
}
